// eslint-disable-next-line @nx/enforce-module-boundaries
import type {NoticeProps} from "@atg-ui/design-system";

export type NoticeVariant = NonNullable<NoticeProps["variant"]>;

export const NoticeVariants = {
    INFORMATION: "information",
    SUCCESS: "success",
    ATTENTION: "attention",
    CRITICAL: "critical",
} as const satisfies Record<string, NoticeVariant>;

// auth types for self exclusion
export const AuthTypes = {
    AtgCaptcha: "atg-captcha",
    BankID: "bankid",
    AuthStarter: "auth-starter",
    FrejaEID: "freja-eid",
    FrejaEIDStarter: "freja-eid-starter",
} as const;
export type AuthType = (typeof AuthTypes)[keyof typeof AuthTypes];

export type Vertical = "CASINO" | "HORSE_BETTING" | "SPORTSBOOK";

export type ActiveGameCategoryFI = "FIXED_ODDS" | "PARIMUTUEL" | "LIVE_CASINO" | "SLOT";

export type SelfExclusionFormattedValue = {
    vertical: Vertical;
    exclusionPeriod: "ONE_MONTH" | "THREE_MONTHS" | "SIX_MONTHS" | "UNTIL_FURTHER_NOTICE";
};

export type SelfExclusionFormattedValueFI = {
    gameCategory: ActiveGameCategoryFI;
    exclusionPeriod: "ONE_MONTH" | "THREE_MONTHS" | "SIX_MONTHS" | "UNTIL_FURTHER_NOTICE";
};

export type SelfExclusionResponse = {
    selfExclusions: SelfExclusions;
};

export type SelfExclusionVerticalData = {
    vertical: Vertical;
    excludedUntil: string | null;
    lockedUntil: string;
    // Self-exclusion set by N: user, J: customer service
    temporaryExclusion: "N" | "J";
    createdBy: string | null;
    createdOn: string;
};

export type NumericSelfExclusionPeriod = 1 | 3 | 6 | 12;

export type SelfExclusionOptionsSE = {
    [K in Vertical]?: NumericSelfExclusionPeriod;
};

export type SelfExclusionOptionsFI = {
    [K in ActiveGameCategoryFI]?: NumericSelfExclusionPeriod;
};

export type SelfExclusionOptions = SelfExclusionOptionsSE | SelfExclusionOptionsFI;

export type SelfExclusionVerticalDataFI = {
    gameCategory: ActiveGameCategoryFI;
    excludedUntil: string | null;
    lockedUntil: string;
    temporaryExclusion: "N" | "J";
    createdBy: string | null;
    createdOn: string;
};

// keep SE-compatible indexing, add FI keys
export type SelfExclusions = Partial<Record<Vertical, SelfExclusionVerticalData>> &
    Partial<Record<ActiveGameCategoryFI, SelfExclusionVerticalDataFI>>;

export type ExclusionMessage = {
    id: string;
    readonly text: string;
    variant: NoticeVariant;
};

export type SetSelfExclusion = (options: SelfExclusionOptions, password: string) => void;

export type SelfExclusionContextOptions = {
    selfExclusion: SelfExclusions;
    setSelfExclusion: SetSelfExclusion;
    toggleSelfExclusionEditMode: () => void;
    isSelfExclusionEditMode: boolean;
    selfExclusionMessage: ExclusionMessage | null;
    clearSelfExclusionMessage: () => void;
};

export type ExclusionState = {
    error: boolean | null;
    isLoading: boolean;
    statusMessage: ExclusionMessage | null;
};

export type SelfExclusionState = {selfExclusions: SelfExclusions} & ExclusionState;

// globalState
export type State = {
    selfExclusion: SelfExclusionState;
};
